import { createContext, useContext } from 'react';

/**
 * Reactified Yandex Maps v3 (ymaps3) bindings. The SDK loads at runtime via a
 * CDN script (see `app/providers/ymaps-provider`), is reactified once, and is
 * exposed here so any widget can consume it WITHOUT importing `app` (the context
 * lives in `@noor/maps`, the provider in `app` — an FSD-legal split, the same
 * shape as cpanel's Redux `ymapsSlice` + `useYMaps`).
 *
 * The bindings are dynamically typed at the CDN boundary; we model the reactify
 * surface loosely (`unknown`-returning module factory) to avoid `any` leaking
 * into call sites. The map widget narrows what it needs.
 */
export interface YmapsBindings {
  /** The raw ymaps3 SDK handle. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDN SDK, dynamically typed
  ymaps3: any;
  /** `reactify` — `.module(ymaps3)` yields React map components. */
  reactify: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDN SDK, dynamically typed
    module: (sdk: unknown) => Record<string, any>;
    /**
     * `reactify.useDefault(value, deps?)` — marks a prop as apply-once (like
     * `defaultValue`): the SDK applies it on mount and ignores it on later
     * renders until `deps` change. REQUIRED for `location` on any map that
     * also flies the camera imperatively — without it, every re-render
     * re-applies the stale prop over the live camera (see
     * `legacy-order-map.tsx`'s mount-camera comment for the mechanism).
     */
    useDefault: <T>(value: T, deps?: readonly unknown[]) => T;
  };
}

export interface YmapsContextValue {
  /** Reactified bindings once the SDK has loaded; null until then. */
  bindings: YmapsBindings | null;
  /** True once the SDK script has loaded and reactify is ready. */
  isReady: boolean;
  /** True when no API key is configured (map should show a placeholder). */
  isDisabled: boolean;
}

export const YmapsContext = createContext<YmapsContextValue>({
  bindings: null,
  isReady: false,
  isDisabled: true,
});

/** Consume the reactified ymaps3 bindings. */
export function useYmaps(): YmapsContextValue {
  return useContext(YmapsContext);
}
